import React, { useState, useRef } from 'react';
import { Input, Select, Button, Checkbox } from '@fluentui/react-components';
import { Add16Filled } from '@fluentui/react-icons';
import { TransactionInput, ACTION_OPTIONS, PayeeSuggestion } from '../types';
import { usePayeeSearch } from '../hooks/usePayeeSearch';
import '../styles/register.css';

interface InputRowProps {
  onAdd: (input: TransactionInput) => void;
  accountId: number | null;
}

function formatMoney(amount: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
}

export const InputRow: React.FC<InputRowProps> = ({ onAdd, accountId }) => {
  const today = new Date().toISOString().split('T')[0];
  const [date, setDate] = useState(today);
  const [action, setAction] = useState('');
  const [payee, setPayee] = useState('');
  const [payment, setPayment] = useState('');
  const [deposit, setDeposit] = useState('');
  const [notes, setNotes] = useState('');
  const [isEom, setIsEom] = useState(false);
  const [acIndex, setAcIndex] = useState(-1);

  const { suggestions, isOpen, close, setQuery } = usePayeeSearch(accountId);
  const payeeRef = useRef<HTMLInputElement>(null);
  const paymentRef = useRef<HTMLInputElement>(null);
  const depositRef = useRef<HTMLInputElement>(null);

  const handlePayeeChange = (val: string) => {
    setPayee(val);
    setQuery(val.trim());
    setAcIndex(-1);
  };

  const selectSuggestion = (s: PayeeSuggestion) => {
    setPayee(s.payee);
    setAction(s.action);
    if (s.payment) {
      setPayment(String(s.payment));
      setDeposit('');
      close();
      setTimeout(() => {
        paymentRef.current?.focus();
        paymentRef.current?.select();
      }, 50);
    } else if (s.deposit) {
      setDeposit(String(s.deposit));
      setPayment('');
      close();
      setTimeout(() => {
        depositRef.current?.focus();
        depositRef.current?.select();
      }, 50);
    } else {
      close();
    }
  };

  const handlePayeeKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setAcIndex(prev => Math.min(prev + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setAcIndex(prev => Math.max(prev - 1, 0));
    } else if ((e.key === 'Enter' || e.key === 'Tab') && acIndex >= 0) {
      e.preventDefault();
      selectSuggestion(suggestions[acIndex]);
    } else if (e.key === 'Escape') {
      close();
    }
  };

  const handleSubmit = () => {
    if (isOpen) return;
    if (!date || !payee.trim()) return;
    const isMemo = action === 'Memo';
    const p = isMemo ? null : (parseFloat(payment) || null);
    const d = isMemo ? null : (parseFloat(deposit) || null);
    if (!isMemo && !p && !d) return;

    onAdd({
      date,
      action: action || 'Draft',
      payee: payee.trim(),
      payment: p,
      deposit: d,
      notes: notes.trim(),
      is_eom: isMemo && isEom,
    });

    setDate(today);
    setAction('');
    setPayee('');
    setPayment('');
    setDeposit('');
    setNotes('');
    setIsEom(false);
    setQuery('');
    close();
  };

  const handleRowKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isOpen) handleSubmit();
  };

  return (
    <tr className="input-row" onKeyDown={handleRowKeyDown}>
      <td className="col-date">
        <Input type="date" value={date} onChange={(e, d) => setDate(d.value)} size="small" />
      </td>
      <td className="col-action">
        <Select value={action} onChange={(e, d) => setAction(d.value)} size="small">
          <option value="">--</option>
          {ACTION_OPTIONS.map(a => <option key={a} value={a}>{a}</option>)}
        </Select>
      </td>
      <td className="col-payee" style={{ position: 'relative' }}>
        <Input
          ref={payeeRef}
          value={payee}
          onChange={(e, d) => handlePayeeChange(d.value)}
          onKeyDown={handlePayeeKeyDown}
          onBlur={() => setTimeout(close, 150)}
          placeholder="Payee"
          size="small"
          autoComplete="off"
        />
        {isOpen && (
          <div className="autocomplete-list">
            {suggestions.map((s, i) => (
              <div
                key={s.payee}
                className={`autocomplete-item ${i === acIndex ? 'active' : ''}`}
                onMouseDown={e => { e.preventDefault(); selectSuggestion(s); }}
              >
                <span className="payee-name">{s.payee}</span>
                <span>
                  <span className="payee-amount">
                    {s.payment ? `-${formatMoney(s.payment)}` : `+${formatMoney(s.deposit!)}`}
                  </span>
                  <span className="payee-action">{s.action}</span>
                </span>
              </div>
            ))}
          </div>
        )}
      </td>
      <td className="col-money">
        <Input ref={paymentRef} type="number" value={payment}
          onChange={(e, d) => setPayment(d.value)} placeholder="0.00"
          size="small" style={{ textAlign: 'right' }} />
      </td>
      <td className="col-money">
        <Input ref={depositRef} type="number" value={deposit}
          onChange={(e, d) => setDeposit(d.value)} placeholder="0.00"
          size="small" style={{ textAlign: 'right' }} />
      </td>
      <td className="col-money col-balance"></td>
      <td className="col-notes">
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <Input value={notes} onChange={(e, d) => setNotes(d.value)}
            placeholder="Notes" size="small" style={{ flex: 1 }} />
        </div>
      </td>
      <td className="col-clr">
        {action === 'Memo' ? (
          <Checkbox
            checked={isEom}
            onChange={(e, d) => setIsEom(!!d.checked)}
            title="End of Month marker"
          />
        ) : null}
      </td>
      <td className="col-actions">
        <Button appearance="primary" size="small" shape="circular"
          icon={<Add16Filled />} onClick={handleSubmit} title="Add transaction"
          style={{ backgroundColor: '#27ae60', minWidth: '32px', minHeight: '32px' }} />
      </td>
    </tr>
  );
};
